import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { HeartPulse, Filter, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/EmptyState";
import { DirectoryEntryCard } from "@/components/DirectoryEntryCard";
import api from "@/lib/api";

export default function AnnuaireSantePage() {
  return <DirectoryTypePage type="sante" typeSlug="sante-performance" />;
}

export function AnnuaireBoutiquesPage() {
  return <DirectoryTypePage type="boutique" typeSlug="boutiques-services" />;
}

// V13.17 — Nouveau type "services" (mêmes composants, même API)
export function AnnuaireServicesPage() {
  return <DirectoryTypePage type="services" typeSlug="services" />;
}

function DirectoryTypePage({ type, typeSlug }) {
  const [items, setItems] = useState([]);
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("category") || "all";
  const activeIsland = params.get("island") || "all";

  // V13.17 — Extension à "services"
  const isSante = type === "sante";
  const isServices = type === "services";
  const sectionLabel = isSante ? "Santé & Performance" : isServices ? "Services aux sportifs" : "Boutiques & Services";
  const sectionSubtitle = isSante
    ? "Professionnels de santé et de performance sportive en Polynésie française."
    : isServices
      ? "Services professionnels aux athlètes, clubs et fédérations polynésiennes."
      : "Commerces, équipementiers et prestataires au service des sportifs polynésiens.";

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const p = { type, limit: 200 };
    if (activeCategory !== "all") p.category = activeCategory;
    if (activeIsland !== "all") p.island = activeIsland;
    Promise.all([
      api.get("/directory", { params: p }),
      taxonomy ? Promise.resolve({ data: taxonomy }) : api.get("/directory/taxonomy"),
    ])
      .then(([resItems, resTax]) => {
        if (!alive) return;
        setItems(resItems.data?.items || []);
        if (!taxonomy) setTaxonomy(resTax.data);
      })
      .catch(() => {
        if (alive) setItems([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, activeCategory, activeIsland]);

  const categories = useMemo(() => {
    if (!taxonomy || !taxonomy[type]) return [];
    return taxonomy[type].categories || [];
  }, [taxonomy, type]);

  const categoryLabelMap = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.slug] = c.label; });
    return m;
  }, [categories]);

  const islands = useMemo(() => {
    const set = new Set();
    items.forEach((it) => { if (it.island) set.add(it.island); });
    return Array.from(set).sort();
  }, [items]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "all" || !value) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <header className="mb-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <HeartPulse className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Annuaire AITO.PF</span>
            </div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl" data-testid={`page-title-${typeSlug}`}>
              {sectionLabel}
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{sectionSubtitle}</p>
          </div>
          <Link to={`/proposer-mon-activite?type=${type}`} data-testid="cta-create-directory-entry">
            <Button className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
              <Plus className="h-4 w-4 mr-1.5" /> Je souhaite référencer mon activité
            </Button>
          </Link>
        </div>
      </header>

      <Card className="p-4 mb-6 bg-background/95 backdrop-blur-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={activeCategory} onValueChange={(v) => updateParam("category", v)}>
            <SelectTrigger className="w-[220px]" data-testid="filter-category">
              <SelectValue placeholder="Toutes les catégories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={activeIsland} onValueChange={(v) => updateParam("island", v)}>
            <SelectTrigger className="w-[180px]" data-testid="filter-island">
              <SelectValue placeholder="Toutes les îles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les îles</SelectItem>
              {islands.map((i) => (
                <SelectItem key={i} value={i}>{i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-muted-foreground">{items.length} fiche{items.length > 1 ? "s" : ""}</div>
        </div>
      </Card>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-64 w-full rounded-lg" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={HeartPulse}
          title={`Aucune fiche ${isSante ? "santé" : isServices ? "services" : "boutique"} pour le moment.`}
          description="Sois le premier à référencer ton activité dans cet annuaire AITO.PF."
          action={
            <Link to={`/proposer-mon-activite?type=${type}`}>
              <Button className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">Je souhaite référencer mon activité</Button>
            </Link>
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((entry) => (
            <DirectoryEntryCard
              key={entry.id}
              entry={entry}
              categoryLabel={categoryLabelMap[entry.category]}
            />
          ))}
        </div>
      )}
    </div>
  );
}
